/**
 * One best-effort GPS fix for a trip event.
 *
 * Location is a nice-to-have on a status tap, never a gate: if the driver says
 * no, the phone has no fix, or the permission prompt is simply left sitting
 * there, the event still goes out — just without coordinates. Resolves to
 * `{ lat, lon }` or `null`; it never rejects.
 *
 * The browser's own `timeout` option does not start counting until the
 * permission prompt is answered, so a separate guard makes sure an ignored
 * prompt cannot hold the status button hostage.
 *
 * WHY THERE IS A SECOND EXPORT. A status tap only needs "a fix or nothing" —
 * it sends either way. The live-location reporter needs to know WHY there was
 * nothing, because the two answers have opposite consequences: a phone with no
 * signal right now is worth asking again in a minute, while a driver who has
 * denied location will never say yes by being asked again, and asking is how a
 * permission prompt ends up on their screen once a minute for a whole shift.
 * So `currentPositionResult` reports the reason and `currentPosition` is a
 * one-line wrapper over it — one implementation, and the status tap's contract
 * is unchanged.
 */

const GEO_TIMEOUT_MS = 5000;
const GUARD_MS = GEO_TIMEOUT_MS + 1000;

/**
 * Why a fix did not arrive.
 *
 * The two PERMANENT ones are what matter: nothing about this phone or this
 * decision will change by asking again, so a caller that polls must stop
 * rather than keep trying. `DENIED` is the driver's own answer (the
 * `PERMISSION_DENIED` code, which browsers also give — immediately, with no
 * prompt — for every later call once an origin is blocked). `UNSUPPORTED` is a
 * browser with no geolocation at all.
 *
 * `UNAVAILABLE` covers everything transient AND the unanswered prompt, which
 * is deliberately not called permanent: the driver has not actually decided.
 */
export const FIX_OK = 'ok';
export const FIX_DENIED = 'denied';
export const FIX_UNSUPPORTED = 'unsupported';
export const FIX_UNAVAILABLE = 'unavailable';

/** Reasons that will never become a fix, however many times we ask. */
export const PERMANENT_FIX_FAILURES = [FIX_DENIED, FIX_UNSUPPORTED];

/** The `PositionError.code` for "the user said no". */
const PERMISSION_DENIED = 1;

/**
 * One fix, with the reason when there is none.
 *
 * Resolves `{ point, reason }` and never rejects. `point` is `{ lat, lon }`
 * only when `reason` is `FIX_OK`, and null otherwise.
 */
export function currentPositionResult() {
  const geolocation = typeof navigator !== 'undefined' ? navigator.geolocation : null;
  if (!geolocation || typeof geolocation.getCurrentPosition !== 'function') {
    return Promise.resolve({ point: null, reason: FIX_UNSUPPORTED });
  }

  return new Promise((resolve) => {
    let settled = false;
    let guard = null;

    const finish = (point, reason) => {
      if (settled) return;
      settled = true;
      if (guard) clearTimeout(guard);
      resolve({ point, reason });
    };

    // The prompt is still sitting there unanswered. Not a decision, so not
    // permanent — but it is also not a fix.
    guard = setTimeout(() => finish(null, FIX_UNAVAILABLE), GUARD_MS);

    try {
      geolocation.getCurrentPosition(
        (position) => {
          const lat = Number(position?.coords?.latitude);
          const lon = Number(position?.coords?.longitude);
          if (Number.isFinite(lat) && Number.isFinite(lon)) finish({ lat, lon }, FIX_OK);
          else finish(null, FIX_UNAVAILABLE);
        },
        (error) => finish(
          null,
          error?.code === PERMISSION_DENIED ? FIX_DENIED : FIX_UNAVAILABLE,
        ),
        { enableHighAccuracy: true, timeout: GEO_TIMEOUT_MS, maximumAge: 30000 },
      );
    } catch {
      finish(null, FIX_UNAVAILABLE);
    }
  });
}

export default async function currentPosition() {
  return (await currentPositionResult()).point;
}
